/**
 * Accessibility settings — applied to <html> as classes / CSS vars.
 *
 * The state lives outside React so settings are read & applied
 * synchronously at boot (in main.tsx, before first paint) and
 * survive every route change. The widget UI subscribes via
 * `subscribe()` and writes via `setSettings()`.
 */

export type A11ySettings = {
  fontScale: number;      // 0..6 → 90% .. 200%
  letterSpacing: boolean;
  lineHeight: boolean;
  highContrast: boolean;
  invertColors: boolean;
  grayscale: boolean;
  lowSaturation: boolean;
  highlightLinks: boolean;
  highlightFocus: boolean;
  reduceMotion: boolean;
  readableFont: boolean;
  bigCursor: boolean;
  hideImages: boolean;
};

const STORAGE_KEY = 'a11y:settings:v2';

// 7 steps: 90, 100, 110, 125, 140, 160, 200 (%)
const SCALES = [0.9, 1.0, 1.1, 1.25, 1.4, 1.6, 2.0];
export const FONT_SCALE_STEPS = SCALES.length;
export const FONT_SCALE_DEFAULT_INDEX = 1; // 100%
export const fontScalePercent = (i: number) =>
  Math.round((SCALES[Math.max(0, Math.min(SCALES.length - 1, i))] ?? 1) * 100);

export const DEFAULTS: A11ySettings = {
  fontScale: FONT_SCALE_DEFAULT_INDEX,
  letterSpacing: false,
  lineHeight: false,
  highContrast: false,
  invertColors: false,
  grayscale: false,
  lowSaturation: false,
  highlightLinks: false,
  highlightFocus: false,
  reduceMotion: false,
  readableFont: false,
  bigCursor: false,
  hideImages: false,
};

let current: A11ySettings = { ...DEFAULTS };
const listeners = new Set<(s: A11ySettings) => void>();

function load(): A11ySettings {
  if (typeof window === 'undefined') return { ...DEFAULTS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<A11ySettings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

function persist(s: A11ySettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

function apply(s: A11ySettings) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const scale = SCALES[s.fontScale] ?? 1;
  // Scaling the root font-size scales every rem-based size in the app.
  root.style.fontSize = scale === 1 ? '' : `${scale * 100}%`;
  root.classList.toggle('a11y-letter-spacing', s.letterSpacing);
  root.classList.toggle('a11y-line-height', s.lineHeight);
  root.classList.toggle('a11y-contrast', s.highContrast);
  root.classList.toggle('a11y-invert', s.invertColors);
  root.classList.toggle('a11y-grayscale', s.grayscale);
  root.classList.toggle('a11y-low-saturation', s.lowSaturation);
  root.classList.toggle('a11y-links', s.highlightLinks);
  root.classList.toggle('a11y-focus', s.highlightFocus);
  root.classList.toggle('a11y-reduce-motion', s.reduceMotion);
  root.classList.toggle('a11y-readable', s.readableFont);
  root.classList.toggle('a11y-big-cursor', s.bigCursor);
  root.classList.toggle('a11y-hide-images', s.hideImages);
}

/** Apply persisted settings as early as possible. Safe to call multiple times. */
export function initA11y() {
  current = load();
  apply(current);
}

export function getSettings(): A11ySettings {
  return current;
}

export function setSettings(updater: (s: A11ySettings) => A11ySettings) {
  current = updater(current);
  apply(current);
  persist(current);
  listeners.forEach((l) => l(current));
}

export function resetSettings() {
  setSettings(() => ({ ...DEFAULTS }));
}

export function subscribe(fn: (s: A11ySettings) => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// Cross-tab sync — if the user toggles in another tab, reflect it here.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    current = load();
    apply(current);
    listeners.forEach((l) => l(current));
  });
}
